import React, { useEffect } from "react";
import {
  Center,
  Box,
  useColorModeValue as mode,
  VStack,
  Text,
  Grid,
  GridItem,
  Divider,
  UnorderedList,
  ListItem,
  Button,
} from "@chakra-ui/react";
import PuffLoader from "react-spinners/PuffLoader";

// Styles
import { textStyle } from "theme/names";

// Utils
import { getPosts, fetchMentionsForProfile } from "controllers/mentions";
import { updateUser } from "utils/sessionHelper";

// Types
import { Profile } from "types/mentions";
import { Post } from "types/post";
import { ReplyComposer } from "components/tweet-composer/replyComposer";
import LinkedinPost from "components/tweets/masonry/linkedinPost";
import toast from "react-hot-toast";

const Posts = ({
  profile,
  account,
  session,
  onSessionUpdate,
}: {
  profile: Profile;
  account: any;
  session: any;
  onSessionUpdate?: () => void;
}) => {
  const [posts, setPosts] = React.useState<Post[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const isOwnProfile = account.id === profile.id;
  console.log("profile", profile);
  const fetchPosts = async () => {
    if (!profile.dashEntityUrn) {
      toast.error("Invalid profile data");
      return;
    }

    setIsLoading(true);
    try {
      const profileId = profile.dashEntityUrn.replace("urn:li:fsd_profile:", "");
      if (!profileId) {
        throw new Error("Invalid profile ID");
      }

      const posts = await getPosts(account.id, profileId, session);
      if (!posts || posts.success === 0) {
        throw new Error("No posts found");
      }
      setPosts(posts);
    } catch (e) {
      console.error("Error fetching posts:", e);
      toast.error(e instanceof Error ? e.message : "Failed to fetch posts. Please try again later");
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const hidePost = (postId: string) => {
    setPosts(posts.filter((post) => post.id_str !== postId));
  };

  const addPost = (post) => {
    const newPosts = [post, ...posts];
    newPosts.sort((a, b) => a.created_at - b.created_at);
    setPosts(newPosts);
  };

  useEffect(() => {
    let isMounted = true;

    const initializeData = async () => {
      try {
        if (isMounted) {
          await fetchPosts();
          await fetchMentionsForProfile(account.idParent, profile, session);
        }
      } catch (e) {
        console.error("Error initializing data:", e);
        toast.error("Failed to initialize data. Please try again later");
      }
    };

    initializeData();

    return () => {
      isMounted = false;
    };
  }, [account.id, account.idParent, profile.dashEntityUrn, session]);

  const enableMentions = () => {
    updateUser(session, { mentionsEnabled: true });
    if (onSessionUpdate) onSessionUpdate();
  };

  if (isOwnProfile && !account.mentionsEnabled)
    return (
      <Center h="200px" flexDirection="column" gap={4}>
        <Text textStyle={textStyle.body.medium.light}>
          To begin viewing your mentions, please enable this feature from here.
        </Text>
        <Button
          variant="primary"
          size="lg"
          borderColor="white"
          onClick={enableMentions}
        >
          Enable @mentions
        </Button>
      </Center>
    );

  if (isLoading)
    return (
      <Center h="200px" flexDirection="column" gap={4}>
        <Text textStyle={textStyle.body.medium.light}>
          Searching for @mentions of {profile.name} from the last 72 hours
        </Text>
        <PuffLoader
          color={"#0A66C2"}
          loading
          cssOverride={{
            display: "block",
            margin: "0 auto",
            borderColor: "red",
          }}
          size={50}
        />
      </Center>
    );
  if (posts.length === 0)
    return (
      <Center h="400px">
        <Box>
          <Text
            as="h2"
            textStyle={textStyle.body.bold.standard}
            fontSize="lg"
            textAlign="center"
            mb={8}
          >
            We did not find any recent mentions of {profile.name} 😢
          </Text>
          <Text textStyle={textStyle.body.medium.light}>
            Are you certain {profile.name} was recently mentioned? Here's why we
            maybe couldn't find them.
          </Text>
          <UnorderedList textStyle={textStyle.body.medium.light} mb={4} pl={4}>
            <ListItem>The @mentions are from more than 72 hours ago</ListItem>
            <ListItem>
              @mentions were made in a comment (we only support @mentions in
              posts)
            </ListItem>
            <ListItem>
              The @mention is in plain regular text and did not in fact tag you
              using the usual @... method
            </ListItem>
          </UnorderedList>
          <Text textStyle={textStyle.body.medium.light} fontWeight="bold">
            Did you find a @mention that we weren't able to find despite those
            conditions?
          </Text>
          <Text textStyle={textStyle.body.medium.light}>
            Send the post URL to our support team so we can check if there's an
            issue.
          </Text>
        </Box>
      </Center>
    );
  return (
    <VStack>
      {posts.map((post) => (
        <Box key={post.id_str}>
          <Grid
            templateColumns={{ lg: "repeat(2, 400px)", md: "repeat(1, 400px)" }}
            columnGap={{ lg: 5, md: 0 }}
            rowGap={5}
            justifyContent="center"
          >
            <GridItem>
              <Box>
                <LinkedinPost
                  savedTweet={[]}
                  tweet={{ data: post }}
                  removeTweet={(p) => hidePost(p.id_str)}
                  refresher={undefined}
                  disableBookmarkButton={false}
                  disableMainHideButton={false}
                  disableHideButton={false}
                  mode="answer"
                  disableRequest={true}
                  disableUserPopup={false}
                />
              </Box>
            </GridItem>
            <GridItem>
              <ReplyComposer
                tweet={post}
                removeTweet={(p) => hidePost(p.id_str)}
                addRemovedTweet={(p) => addPost(p)}
              />
            </GridItem>
          </Grid>
          <Divider mt={5} mb={5} />
        </Box>
      ))}
    </VStack>
  );
};

export default Posts;
